import { useFormik } from "formik";
import {
  REQUIRED_MSG,
} from "../schema/formik";
import * as Yup from "yup";

export interface MisdeliveryBolFormValues {
  pickupNumber?: number;
  pickupBusinessName?: string;
  pickupAddress1?: string;
  pickupAddress2?: string;
  pickupCity?: string;
  pickupState?: string;
  pickupZip?: string;
  pickupPhoneNumber?: number | string;
  pickupContactName?: string;
  freightDescription?: string;
  isMisdeliveryHazmat?: string;
  unNumber?: string;
  unNumberSequence?: number;
  totalPiecesMisdelivered?: number;
  weight?: number;
  proNumber?: number;
  misdeliveredPalletPro?: number;
  terminalId?: string;
  consigneeName?: string;
  consigneeAddress1?: string;
  consigneeAddress2?: string;
  consigneeCity?: string;
  consigneeState?: string;
  consigneeZip?: string;
  misdeliveredBolPro?: number;
}

export const misdeliveryBolInitialValues: MisdeliveryBolFormValues = {
  pickupNumber: undefined,
  pickupBusinessName: undefined,
  pickupAddress1: undefined,
  pickupAddress2: undefined,
  pickupCity: undefined,
  pickupState: undefined,
  pickupZip: undefined,
  pickupPhoneNumber: undefined,
  pickupContactName: undefined,
  freightDescription: undefined,
  isMisdeliveryHazmat: undefined,
  unNumber: undefined,
  unNumberSequence: undefined,
  totalPiecesMisdelivered: undefined,
  weight: undefined,
  proNumber: undefined,
  misdeliveredPalletPro: undefined,
  terminalId: undefined,
  consigneeName: "SAIA OS&D",
  consigneeAddress1: undefined,
  consigneeAddress2: undefined,
  consigneeCity: undefined,
  consigneeState: undefined,
  consigneeZip: undefined,
  misdeliveredBolPro: undefined,
};

export const misdeliveryBolValidationSchema = Yup.object().shape({
  pickupNumber: Yup.number().nullable(),
  pickupBusinessName: Yup.string().required(REQUIRED_MSG),
  pickupAddress1: Yup.string().required(REQUIRED_MSG),
  pickupAddress2: Yup.string(),
  pickupCity: Yup.string().required(REQUIRED_MSG),
  pickupState: Yup.string(),
  pickupZip: Yup.string().required(REQUIRED_MSG),
  pickupPhoneNumber: Yup.string(),
  pickupContactName: Yup.string(),
  freightDescription: Yup.string().required(REQUIRED_MSG),
  isMisdeliveryHazmat: Yup.string().required(REQUIRED_MSG),
  unNumber: Yup.string().when("isMisdeliveryHazmat", {
    is: (val: string) => val === "Yes",
    then: (schema) => schema.required(REQUIRED_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),
  unNumberSequence: Yup.number(),
  totalPiecesMisdelivered: Yup.number().required(REQUIRED_MSG),
  weight: Yup.number().required(REQUIRED_MSG),
  proNumber: Yup.number().nullable(),
  misdeliveredPalletPro: Yup.number().nullable(),
  terminalId: Yup.string().required(REQUIRED_MSG),
  commodityCode: Yup.string(),
  consigneeName: Yup.string(),
  consigneeAddress1: Yup.string(),
  consigneeAddress2: Yup.string(),
  consigneeCity: Yup.string(),
  consigneeState: Yup.string(),
  consigneeZip: Yup.string(),
  misdeliveredBolPro: Yup.number()
    .typeError(REQUIRED_MSG)
    .required(REQUIRED_MSG)
    .test(
      "not-match-pallet",
      "Pallet PRO# must be different from Applied to PRO",
      function (value) {
        if (value === undefined || value === null) return true;
        return value !== this.parent.misdeliveredPalletPro;
      }
    )
    .test(
      "not-match-pro",
      "PRO# must be different from Misdelivered on PRO (Master PRO)",
      function (value) {
        if (value === undefined || value === null) return true;
        return value !== this.parent.proNumber;
      }
    )
    .min(10000000000, "PRO# must be at least 11 digits")
    .max(999999999999, "PRO# must be at most 12 digits")
    .test(
      "length-11-or-12",
      "PRO# must be 11 or 12 digits",
      function (value) {
        if (value === undefined || value === null) return true;
        const str = value.toString();
        return str.length === 11 || str.length === 12;
      }
    ),
});

export interface UseMisdeliveryBolFormOptions {
  onSubmit?: (values: MisdeliveryBolFormValues) => void | Promise<void>;
}

export const useMisdeliveryBolForm = (
  initialData?: Partial<MisdeliveryBolFormValues>,
  options: UseMisdeliveryBolFormOptions = {},
) => {
  const formik = useFormik<MisdeliveryBolFormValues>({
    initialValues: {
      ...misdeliveryBolInitialValues,
      ...initialData,
    },
    validationSchema: misdeliveryBolValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (options.onSubmit) {
        await options.onSubmit(values);
      }
    },
  });

  return { formik };
};
